import React from 'react';
import { Switch, Text } from '@chatsdet/ui';
import { Warning } from '@phosphor-icons/react';
import { MacroRichTextField } from './MacroRichTextField';

type NavigateNodeConfigProps = {
  localData: Record<string, any>;
  setLocalData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
};

/** Matches values that look like a domain (with or without protocol). */
const FULL_URL_RE = /^(https?:\/\/|[a-z0-9]([a-z0-9-]*[a-z0-9])?\.)/i;

export const NavigateNodeConfig: React.FC<NavigateNodeConfigProps> = ({
  localData,
  setLocalData,
}) => {
  const handleChange = React.useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) {
        setLocalData((prev) => {
          const {
            canonicalPageId: _1,
            pageUrl: _2,
            pageTitle: _3,
            parentPageId: _4,
            parentPageTitle: _5,
            parentPageUrl: _6,
            ...rest
          } = prev;
          return rest;
        });
        return;
      }

      // Normalize bare paths: "tickets" → "/tickets"
      // Skip macros ($var.), full URLs, and already-absolute paths
      let normalized = trimmed;
      if (!FULL_URL_RE.test(normalized) && !normalized.startsWith('/') && !normalized.startsWith('$')) {
        normalized = `/${normalized}`;
      }

      setLocalData((prev) => {
        const {
          canonicalPageId: _1,
          pageTitle: _2,
          parentPageId: _3,
          parentPageTitle: _4,
          parentPageUrl: _5,
          ...rest
        } = prev;
        return { ...rest, pageUrl: normalized };
      });
    },
    [setLocalData]
  );

  const isFullUrl = localData.pageUrl && FULL_URL_RE.test(localData.pageUrl);

  return (
    <div className="space-y-4">
      <MacroRichTextField
        label="Destination page"
        placeholder="Enter a path like /dashboard or dashboard…"
        value={localData.pageUrl || ''}
        onChange={handleChange}
      />

      {isFullUrl && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20 px-3 py-2">
          <Warning size={14} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
          <Text size="xs" className="text-amber-700 dark:text-amber-400">
            Full URLs navigate to the exact domain regardless of environment. Use a path
            (e.g. <span className="font-mono">/dashboard</span>) to stay on the current domain.
          </Text>
        </div>
      )}

      <div className="space-y-1">
        <Switch
          size="sm"
          isSelected={!!localData.openInNewTab}
          onChange={(isSelected) =>
            setLocalData((prev) => ({ ...prev, openInNewTab: isSelected }))
          }
          label={
            <Text size="xs" className="text-muted-foreground">
              Open in new tab
            </Text>
          }
        />
        <Text size="xs" className="text-muted-foreground">
          Open this page in a new tab before continuing.
        </Text>
      </div>
    </div>
  );
};
